import * as React from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { isDefined } from '@/utilities/guards';
import { cn } from '@/lib/utils';

const badgeColors = {
  default:
    'bg-neutral-900 text-ui-base hover:bg-neutral-900/80 dark:bg-ui-base dark:text-neutral-900 dark:hover:bg-ui-base/80',
  secondary: 'bg-neutral-100 text-text-gray hover:bg-neutral-100/80 dark:bg-neutral-800 dark:hover:bg-neutral-800/80',
  destructive:
    'bg-red-500 text-ui-base hover:bg-red-500/80 dark:bg-red-900 dark:text-text-base dark:hover:bg-red-900/80',
  outline: 'border border-icon-base text-icon-base',
  dark: 'bg-icon-dark text-text-inverted',
  light: 'bg-ui-light text-text-gray',
  medium: 'bg-ui-medium text-text-gray',
  base: 'bg-icon-base text-text-inverted',
  warning: 'bg-status-warning text-text-inverted',
  warningDark: 'bg-status-warning-dark text-black',
  sharing: 'border-transparent bg-feature-sharing text-text-inverted',
  highlight: 'bg-highlight-base text-text-inverted',
  album: 'bg-album-color text-text-inverted',
  failure: 'bg-status-failure text-text-inverted',
  bevel: 'bg-ui-bevel text-text-gray',
};

export type BadgeVariant = keyof typeof badgeColors;

// Single source of truth for size: the cva `size` variant (below) and the
// label|value segment padding both read from this map, so adding a size is one
// entry. Padding + text only — `rounded-xl` lives on the cva base so the inner
// value segments don't inherit rounding at the label|value junction.
const badgeSizes = {
  default: 'px-2 py-0.5 text-[10px] leading-none!',
  md: 'px-1.5 text-[12px]',
  sm: 'px-1 py-0.5 text-[10px] rounded-sm',
} as const;

const badgeVariants = cva('inline-flex items-center rounded-xl font-semibold transition-colors focus:outline-hidden', {
  variants: {
    size: badgeSizes,
    variant: badgeColors,
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /** Optional right-hand segment turning the badge into a label|value pair, e.g. Cover|3000 x 3000 */
  value?: React.ReactNode;
  /** Variant for the `value` segment. Defaults to `light`, or `outline` to match an `outline` label. */
  valueVariant?: BadgeVariant;
  /** Extra classes for the `value` segment; `className` styles the label segment. */
  valueClassName?: string;
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, value, valueVariant, valueClassName, children, ...props }, ref) => {
    const hasValue = isDefined(value);

    const badge = (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant, size }), hasValue && ' rounded-r-none!', className)}
        {...props}
      >
        {children}
      </div>
    );

    return hasValue ? (
      /**
       * Grid, not flex: auto tracks grow equally from min-content, keeping a short
       * segment's text when squeezed (flex would starve it). w-fit stops a lone badge
       * from filling a stretching parent.
       */
      <div className="grid w-fit max-w-full min-w-0 grid-cols-[minmax(0,auto)_minmax(0,auto)] overflow-hidden">
        {badge}
        <Badge
          variant={valueVariant ?? (variant === 'outline' ? 'outline' : 'light')}
          className={cn(
            'flex items-center rounded-l-none!',
            badgeSizes[size ?? 'default'],
            variant === 'outline' && 'border-l-0',
            valueClassName,
          )}
        >
          {value}
        </Badge>
      </div>
    ) : (
      badge
    );
  },
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
